/**
 * Working out when the dancing actually starts.
 *
 * Plenty of socials open with a class, so the time on the listing is when the
 * doors open for a lesson, not when there is a floor to dance on. Someone
 * planning two dances in a night needs the second number, and it is the one
 * nobody prints.
 *
 * Open dancing starts when the lesson ends — that is the rule, and it is the
 * whole of the derivation. What this module adds is the honesty around it:
 * saying nothing when the data cannot support the claim, and not inventing a
 * dancing slot for an evening that is only ever a class.
 */

import { timeToMinutes } from "./dateUtils";

/** The tag organizers set when a listing includes a class. */
export const LESSON_TAG = "lesson-included";

/** The part of an event this module reads. */
export interface LessonFields {
  startTime: string;
  endTime?: string;
  lessonStartTime?: string;
  lessonEndTime?: string;
  tags?: string[];
}

export type LessonSchedule =
  /** No lesson: the dancing starts when the event does, so there is nothing to add. */
  | { kind: "none" }
  /**
   * A lesson is advertised but not timed, so when the floor opens is unknown.
   * Worth flagging — arriving at the door time may mean walking into a class —
   * but no time may be shown, because none was given.
   */
  | { kind: "lesson-untimed" }
  /** The class runs to the end of the event; there is no open dancing to point at. */
  | { kind: "lesson-only" }
  /** A lesson, then dancing from `danceStart`. */
  | { kind: "timed"; lessonStart?: string; danceStart: string };

/**
 * Minutes from the event's start, carrying a time past midnight forward.
 *
 * A milonga listed 8pm–1am ends at minute 60 of the following day, not 23
 * hours before it began; comparing the raw clock values would make its 9pm
 * lesson look like it ran past the end of the night.
 */
function minutesFromStart(time: string, startMin: number): number {
  const raw = timeToMinutes(time);
  return raw < startMin ? raw + 1440 : raw;
}

/** When the floor opens, as far as this event's data can say. */
export function getLessonSchedule(event: LessonFields): LessonSchedule {
  const advertised = event.tags?.includes(LESSON_TAG) === true || Boolean(event.lessonStartTime);

  if (!event.lessonEndTime) return advertised ? { kind: "lesson-untimed" } : { kind: "none" };

  const startMin = timeToMinutes(event.startTime);
  const lessonEndMin = minutesFromStart(event.lessonEndTime, startMin);

  // A lesson that ends when the event starts tells us nothing about the floor.
  if (lessonEndMin <= startMin) return advertised ? { kind: "lesson-untimed" } : { kind: "none" };

  if (event.endTime) {
    const endMin = minutesFromStart(event.endTime, startMin);
    if (lessonEndMin >= endMin) return { kind: "lesson-only" };
  }

  return {
    kind: "timed",
    ...(event.lessonStartTime ? { lessonStart: event.lessonStartTime } : {}),
    danceStart: event.lessonEndTime,
  };
}
